import React, {
    ReactElement, ReactNode, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { RiArrowLeftRightLine, RiUserForbidLine, RiUserSharedLine } from "@remixicon/react";
import classNames from "classnames";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import { AssignmentType, Ticket } from "../../../contexts/selection-state/selection-state";
import useSelectionState from "../../../contexts/selection-state/SelectionStateContext";
import {
    ChatStatus, GetChatDocument, Queue, useAssignChatsMutation, User, useReopenChatsMutation, useTransferSettingsQuery,
} from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import Confirm from "../../confirm/Confirm";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";
import { useRegisterAction, useTicketState } from "../../../contexts/ticket-state";
import {
    ActionContext, ActionHandler, ActionType, HANDLER_INTERFACE,
} from "../../../contexts/ticket-state/action-registry";
import { useTheme } from "../../../contexts/Theme";

enum Type {
    TRANSFER,
    ASSIGN,
    UNASSIGN,
    TAKE_OVER,
    REOPEN,
    UNIGNORE
}

const TITLE_TEXT = {
    [Type.TRANSFER]: "Transfer ticket",
    [Type.ASSIGN]: "Assign ticket",
    [Type.UNASSIGN]: "Unassign ticket",
    [Type.TAKE_OVER]: "Take over ticket",
    [Type.REOPEN]: "Reopen ticket",
    [Type.UNIGNORE]: "Unignore ticket",
};

const BUTTON_TEXT = {
    [Type.TRANSFER]: "Transfer ticket",
    [Type.ASSIGN]: "Assign ticket",
    [Type.UNASSIGN]: "Unassign ticket",
    [Type.TAKE_OVER]: "Take over ticket",
    [Type.REOPEN]: "Reopen ticket",
    [Type.UNIGNORE]: "Unignore ticket",

};

function isUser(user: User | Queue): user is User {
    return user.__typename === "User";
}
export default function Transfer(): ReactNode {
    const selectionState = useSelectionState();
    const ticketContext = useTicketState();
    const [assignChat] = useAssignChatsMutation();
    const [reopenChat] = useReopenChatsMutation();
    const [actionHandler, setActionHandler] = useState<ActionHandler>();

    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [type, setType] = useState<Type>(Type.TRANSFER);
    const canAddAssignee = (type === Type.ASSIGN || type === Type.TRANSFER);

    const { data, loading, error } = useTransferSettingsQuery({
        skip: !canAddAssignee,
    });

    const theme = useTheme();

    const [message, setMessage] = useState<string>("");
    const [selectedAgent, setSelectedAgent] = useState<User | Queue>();
    const selectRef = React.createRef<SelectInstanceHandler>();
    const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);

    useEffect(() => {
        if (shouldShow) {
            selectRef.current?.focus();
        }
        setSelectedAgent(undefined);
        setMessage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    const transfer = async () => {
        setShouldShow(false);

        const isTransfer = type === Type.TRANSFER;

        if (isTransfer) {
            // selectionState.delete(...selectedTicketIds);
            selectionState.closeCurrent();
        }
        if (type === Type.REOPEN || type === Type.UNIGNORE) {
            actionHandler?.items.forEach(ticket => ticket.setStatusLocal(ChatStatus.Assigned));

            reopenChat({
                refetchQueries: isTransfer ? selectedTicketIds.map(id => ({
                    query: GetChatDocument,
                    variables: { id },
                })) : undefined,
                variables: {
                    ids: selectedTicketIds,
                },
            });
            selectionState.setChatStatuses(selectedTicketIds, ChatStatus.Assigned, AssignmentType.CURRENT_USER);
            actionHandler?.done();
        } else {
            if (type === Type.UNASSIGN) {
                actionHandler?.items.forEach(ticket => {
                    ticket.assignLocal({
                        shouldUnassign: true,
                    });
                    ticket.closeIfOpen();
                });
            } else {
                actionHandler?.items.forEach(ticket => {
                    ticket.assignLocal({
                        assigneeId: selectedAgent?.id,
                    });
                    ticket.closeIfOpen();
                });
            }
            actionHandler?.done();

            await assignChat({
                refetchQueries: isTransfer ? selectedTicketIds.map(id => ({
                    query: GetChatDocument,
                    variables: { id },
                })) : undefined,
                variables: {
                    ids: selectedTicketIds,
                    assignment: {
                        assigneeId: selectedAgent?.id,
                        shouldAssignSelf: type === Type.TAKE_OVER,
                        shouldUnassign: type === Type.UNASSIGN,
                        message,
                    },
                },
            });
            selectionState.setChatStatuses(
                selectedTicketIds,
                type === Type.UNASSIGN ? ChatStatus.ChatRequested : ChatStatus.Assigned,
                type === Type.TAKE_OVER ? AssignmentType.CURRENT_USER : AssignmentType.OTHER_USER,
            );
        }
    };

    const startAssign = (tickets?: Ticket[]) => {
        const selected = tickets
            ?? ticketContext?.selectionState.current
            ?? selectionState.getSelection().filter(row => row.status === ChatStatus.ChatRequested
                || row.status === ChatStatus.Connected);
        setSelectedTicketIds(selected.map(row => row.id));
        if (selected.isNotEmpty) {
            setType(Type.ASSIGN);
            setShouldShow(true);
        }
    };

    const startTransfer = (tickets?: Ticket[]) => {
        const selected = tickets
            ?? ticketContext?.selectionState.current
            ?? selectionState.getSelection().filter(row => row.status === ChatStatus.Assigned
                || row.status === ChatStatus.OnHold || row.status === ChatStatus.Snoozed);
        setSelectedTicketIds(selected.map(row => row.id));
        if (selected.isNotEmpty) {
            setType(Type.TRANSFER);
            setShouldShow(true);
        }
    };

    const startTakeOver = (tickets?: Ticket[]) => {
        const selected = tickets
            ?? ticketContext?.selectionState.current
            ?? selectionState.getSelection().filter(row => !row.isAssignedToCurrentUser);
        setSelectedTicketIds(selected.map(row => row.id));
        if (selected.isNotEmpty) {
            setType(Type.TAKE_OVER);
            setShouldShow(true);
        }
    };

    const startReopen = (tickets?: Ticket[]) => {
        const selected = tickets
            ?? ticketContext?.selectionState.current
            ?? selectionState.getSelection().filter(row => row.isAssignedToCurrentUser);
        setSelectedTicketIds(selected.map(row => row.id));
        if (selected.isNotEmpty) {
            setType(selected.firstElement?.status === ChatStatus.Ignored ? Type.UNIGNORE : Type.REOPEN);
            setShouldShow(true);
        }
    };

    const startAssignSelf = async (selected: Ticket[], handler?: ActionHandler) => {
        const ids = selected.toIdList("id");
        setSelectedTicketIds(ids);
        (handler ?? actionHandler)?.done();
        if (selected.isNotEmpty) {
            await assignChat({
                variables: {
                    ids,
                    assignment: {
                        shouldAssignSelf: true,
                    },
                },
            });
            selectionState.setChatStatuses(ids, ChatStatus.Assigned, AssignmentType.CURRENT_USER);
        }
    };

    const startUnassign = async (tickets?: Ticket[]) => {
        const selected = tickets
            ?? ticketContext?.selectionState.current
            ?? selectionState.getSelection().filter(row => row.isAssignedToCurrentUser);
        setSelectedTicketIds(selected.map(row => row.id));
        if (selected.isNotEmpty) {
            setType(Type.UNASSIGN);
            setShouldShow(true);
        }
    };

    useRegisterAction({
        createBulkActionButton(items) {
            return items.some(item => item.status === ChatStatus.ChatRequested) ? {
                text: "Assign",
                icon: <RiUserSharedLine />,
                variant: "ghost-gray",
            } : undefined;
        },
        handleBulkAction(handler) {
            setActionHandler(handler);
            startAssign(handler.items.map(row => row.chat));
        },
    }, ActionType.ASSIGN);

    useRegisterAction({
        handleBulkAction(handler) {
            setActionHandler(handler);
            startAssignSelf(handler.items.map(row => row.chat), handler);
        },
    }, ActionType.ASSIGN_SELF);

    useRegisterAction({
        createBulkActionButton(items) {
            return items.some(item => item.status === ChatStatus.Assigned) ? {
                text: "Unassign",
                icon: <RiUserForbidLine />,
                variant: "ghost-gray",
            } : undefined;
        },
        handleBulkAction(handler) {
            setActionHandler(handler);
            startUnassign(handler.items.map(row => row.chat));
        },
    }, ActionType.UNASSIGN);

    useRegisterAction({
        createBulkActionButton(items) {
            return items.some(item => item.status === ChatStatus.Assigned
                || item.status === ChatStatus.OnHold
                || item.status === ChatStatus.Snoozed)
                ? {
                    text: "Transfer",
                    icon: <RiUserSharedLine />,
                    variant: "ghost-gray",
                } : undefined;
        },
        handleBulkAction(handler) {
            setActionHandler(handler);
            startTransfer(handler.items.map(row => row.chat));
        },
    }, ActionType.TRANSFER);

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+t",
            context: "default",
            description: "Transfers the conversation",
            group: "Conversations",
            handler: () => {
                startTransfer();
                setActionHandler(ticketContext?.selectionState.createActionHandler(ActionContext.BULK_ACTION));
            },
        });
        shortcuts.on({
            shortcut: "k+a",
            context: "default",
            description: "Assigns the conversation",
            group: "Conversations",
            handler: () => {
                startAssign();
                setActionHandler(ticketContext?.selectionState.createActionHandler(ActionContext.BULK_ACTION));
            },
        });
        shortcuts.on({
            shortcut: "k+u",
            context: "default",
            description: "Unassigns the conversation",
            group: "Conversations",
            handler: () => {
                startUnassign();
                setActionHandler(ticketContext?.selectionState.createActionHandler(ActionContext.BULK_ACTION));
            },
        });
    });

    selectionState.registerAssignHandler(startAssign);
    selectionState.registerTransferHandler(startTransfer);
    selectionState.registerTakeOverHandler(startTakeOver);
    selectionState.registerReopenHandler(startReopen);
    selectionState.registerUnassignHandler(startUnassign);

    selectionState.registerAssignSelfHandler(startAssignSelf);

    if (loading) {
        return undefined;
    }

    const agentName = (selectedAgent as User)?.firstName ? ` ${(selectedAgent as User).firstName}` : "";
    let placeholder = (!canAddAssignee || !agentName)
        ? "Let me help out with this one.."
        : `Hey${agentName}, can you take a look at this one?`;
    if (type === Type.UNASSIGN) {
        placeholder = "I need to put this back into the queue because...";
    }
    type AssigneeOptions = Option<Partial<User | Queue>>[] | OptionWithChildren<Partial<User | Queue>>[];
    let assigneeOptions: AssigneeOptions = data?.settings?.agents?.map(agent => ({
        label: (
            <>
                <Avatar src={agent.avatarUrl as string} variant="xs" />
                {agent.name}
            </>
        ),
        value: agent.id,
        data: agent,
    })) ?? [];
    if (data?.queues.isNotEmpty) {
        assigneeOptions = [
            {
                label: "Queues",
                options: data.queues.map(queue => ({
                    label: queue.name ?? "",
                    value: queue.id,
                    data: queue,
                })),
            },
            {
                label: "Assignees",
                options: assigneeOptions,
            },
        ] as OptionWithChildren<Partial<User | Queue>>[];
    }
    return (
        <Confirm
            confirmText={BUTTON_TEXT[type]}
            shouldShow={shouldShow}
            closeText="Close"
            title={TITLE_TEXT[type]}
            onHide={() => {
                actionHandler?.rollback();
                setShouldShow(false);
            }}
            onConfirm={() => transfer()}
        >
            <div className={classNames(theme.isV3 ? "" : "p-3", "flex-grow-1 d-flex flex-column")}>
                {canAddAssignee && (
                    <div className="mb-4">
                        <Form.Label>Assignee</Form.Label>
                        <Select
                            ref={selectRef}
                            placeholder="Assignee"
                            options={assigneeOptions}
                            filterOption={(user, input) => (input
                                ? user.name?.toLowerCase().includes(input.toLowerCase())
                                : true) ?? true}
                            onChange={selected => setSelectedAgent(selected.data as User)}
                            value={selectedAgent ? {
                                label: !isUser(selectedAgent) ? selectedAgent.name : (
                                    <>
                                        <Avatar src={selectedAgent.avatarUrl as string} variant="xs" />
                                        {selectedAgent.name}
                                    </>
                                ),
                                value: selectedAgent.id,
                                data: selectedAgent,
                            } : undefined}
                        />
                        <Form.Text>
                            Any additional comments or context you want to leave with the new assignee
                        </Form.Text>

                    </div>
                )}

                <Form.Label>
                    {canAddAssignee && "Assignee"}
                    {" "}
                    Comments
                </Form.Label>
                <Form.Control
                    as="textarea"
                    type="text"
                    placeholder={placeholder}
                    className="default-text-height p-2"
                    onChange={evt => setMessage(evt.target.value)}
                />
                <Form.Text>
                    {!canAddAssignee
                        ? "Any additional comments you want to leave on the ticket"
                        : "Any additional comments or context you want to leave for the new assignee"}

                </Form.Text>

            </div>
        </Confirm>
    );
}

HANDLER_INTERFACE.register(Transfer);

Transfer.defaultProps = {
    refreshData: () => {
        // nothing
    },
};
